import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Alert, Badge, Col, Form, FormGroup, Input, Label, Row, Spinner } from 'reactstrap';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useAppDispatch, useAppSelector } from 'hooks';

import { getErrorMessage } from '~libs/axiosWrapper';
import { dateDisplayFormat } from '~libs/dayjs';

import { Button, ContainerHeader, ErrorResponse } from '~components';
import { DateInput, MultiSelect } from '~components/form-fields/inputs';
import { Row as TableViewRow, TableView } from '~components/table-view';
import { setTitle } from '~main/actions/login';
import { getAll as getServiceTypes } from '~appointments/actions/serviceTypes';
import { selectAppointmentServiceTypes } from '~appointments/selectors/serviceTypes';

import { useLazyGetDexReadinessQuery } from '../actions/dex';
import { readinessColumns } from '../forms/readinessList';
import { DexReadinessParams } from '../types';

interface FilterValues {
  date_from: string;
  date_to: string;
  client_name: string;
  care_worker: string;
  service_type_id: number[];
}

const INITIAL_VALUES: FilterValues = {
  date_from: '',
  date_to: '',
  client_name: '',
  care_worker: '',
  service_type_id: [],
};

const PAGE_SIZE = 100;

const validationSchema = Yup.object({
  date_from: Yup.string()
    .required('From date is required')
    .test('date-range', 'From date must be before to date', function (date_from) {
      const { date_to } = this.parent as FilterValues;
      return !date_from || !date_to || date_from <= date_to;
    }),
  date_to: Yup.string().required('To date is required'),
});

const buildParams = (values: FilterValues, offset = 0): DexReadinessParams => {
  const params: DexReadinessParams = {
    date_from: values.date_from,
    date_to: values.date_to,
    limit: PAGE_SIZE,
    offset,
  };
  if (values.client_name) params.client_name = values.client_name;
  if (values.care_worker) params.care_worker = values.care_worker;
  if (values.service_type_id.length) params.service_type_id = values.service_type_id;
  return params;
};

const DexReadinessPage: React.FC = () => {
  useEffect(() => {
    setTitle('DEX Readiness Report');
  }, []);

  const dispatch = useAppDispatch();
  const rawServiceTypes = useAppSelector(selectAppointmentServiceTypes);
  const dexServiceTypes = useMemo(() => rawServiceTypes.filter((s) => s.dex_code), [rawServiceTypes]);

  useEffect(() => {
    dispatch(getServiceTypes());
  }, [dispatch]);

  const [searchParams] = useSearchParams();
  const prefillFrom = searchParams.get('date_from') ?? '';
  const prefillTo = searchParams.get('date_to') ?? '';

  const [triggerReadiness, { data, isFetching, error }] = useLazyGetDexReadinessQuery();
  const [lastSubmittedValues, setLastSubmittedValues] = useState<FilterValues | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  const formik = useFormik<FilterValues>({
    initialValues: { ...INITIAL_VALUES, date_from: prefillFrom, date_to: prefillTo },
    validationSchema,
    onSubmit: async (values) => {
      setLastSubmittedValues(values);
      setCurrentPage(1);
      await triggerReadiness(buildParams(values, 0));
    },
  });

  const handleDateChange = (id: string, value: string | null) => {
    formik.setFieldValue(id, value ?? '');
    formik.setFieldTouched(id, true, false);
  };

  const handlePageChange = async (page: number) => {
    if (!lastSubmittedValues) return;
    setCurrentPage(page);
    await triggerReadiness(buildParams(lastSubmittedValues, (page - 1) * PAGE_SIZE));
  };

  const hasAutoTriggered = useRef(false);
  useEffect(() => {
    if (!hasAutoTriggered.current && prefillFrom && prefillTo) {
      const prefillValues: FilterValues = { ...INITIAL_VALUES, date_from: prefillFrom, date_to: prefillTo };
      if (validationSchema.isValidSync(prefillValues)) {
        hasAutoTriggered.current = true;
        setLastSubmittedValues(prefillValues);
        triggerReadiness(buildParams(prefillValues, 0));
      }
    }
  }, [prefillFrom, prefillTo, triggerReadiness]);

  const summary = data?.summary;

  // One flat table row per incomplete appointment - no series grouping. The action cell resolves
  // row.appointment_id and every display column comes straight off the row.
  const readinessRows = useMemo((): TableViewRow[] => {
    if (!data) return [];
    return data.rows.map((row) => ({ ...row, id: row.appointment_id }));
  }, [data]);

  return (
    <div className="dex-readiness-page p-2" data-cy="dex-readiness-page">
      <ContainerHeader dataCy="dex-readiness-header">DEX Readiness Report</ContainerHeader>

      <div className="p-2 ps-4 pe-4">
        <p className="text-muted mb-3">
          Find appointments with missing Stage 3 DEX data before running the DEX export.
        </p>

        <Form onSubmit={formik.handleSubmit} data-cy="dex-readiness-filters">
          <Row>
            <Col md={3}>
              <FormGroup>
                <Label for="date_from">From Date</Label>
                <DateInput
                  type="date"
                  id="date_from"
                  name="date_from"
                  value={formik.values.date_from}
                  onChange={handleDateChange}
                  dataCy="dex-readiness-from-date"
                  maxDate={formik.values.date_to || undefined}
                  displayFormat={dateDisplayFormat}
                />
                {formik.touched.date_from && formik.errors.date_from && (
                  <div className="invalid-feedback d-block" data-cy="dex-readiness-from-date-error">
                    {formik.errors.date_from}
                  </div>
                )}
              </FormGroup>
            </Col>
            <Col md={3}>
              <FormGroup>
                <Label for="date_to">To Date</Label>
                <DateInput
                  type="date"
                  id="date_to"
                  name="date_to"
                  value={formik.values.date_to}
                  onChange={handleDateChange}
                  dataCy="dex-readiness-to-date"
                  minDate={formik.values.date_from || undefined}
                  displayFormat={dateDisplayFormat}
                />
                {formik.touched.date_to && formik.errors.date_to && (
                  <div className="invalid-feedback d-block" data-cy="dex-readiness-to-date-error">
                    {formik.errors.date_to}
                  </div>
                )}
              </FormGroup>
            </Col>
            <Col md={3}>
              <FormGroup>
                <Label for="client_name">Client Name</Label>
                <Input
                  type="text"
                  id="client_name"
                  name="client_name"
                  value={formik.values.client_name}
                  onChange={formik.handleChange}
                  placeholder="Search client..."
                  data-cy="dex-readiness-client-name"
                />
              </FormGroup>
            </Col>
            <Col md={3}>
              <FormGroup>
                <Label for="care_worker">Care Worker</Label>
                <Input
                  type="text"
                  id="care_worker"
                  name="care_worker"
                  value={formik.values.care_worker}
                  onChange={formik.handleChange}
                  placeholder="Search care worker..."
                  data-cy="dex-readiness-care-worker"
                />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <FormGroup>
                <Label for="service_type_id">Service Type</Label>
                <div data-cy="dex-readiness-service-type">
                  <MultiSelect
                    type="select"
                    id="service_type_id"
                    name="service_type_id"
                    value={formik.values.service_type_id}
                    options={dexServiceTypes}
                    placeholder="All"
                    setFieldValue={formik.setFieldValue}
                  />
                </div>
              </FormGroup>
            </Col>
            <Col md={3} className="d-flex align-items-end pb-3">
              <Button
                type="submit"
                color="primary"
                disabled={isFetching}
                data-cy="dex-readiness-search-button"
              >
                {isFetching ? <Spinner size="sm" /> : 'Search'}
              </Button>
            </Col>
          </Row>
        </Form>

        {error && (
          <ErrorResponse message={getErrorMessage(error)} dataCy="dex-readiness-error" />
        )}

        {data && summary && !error && (
          <Alert
            color={summary.total === 0 ? 'success' : 'warning'}
            className="mt-2"
            data-cy="dex-readiness-summary"
          >
            {summary.total === 0 ? (
              'All DEX data complete - no appointments have missing Stage 3 data in the selected date range.'
            ) : (
              <>
                <strong>{summary.total}</strong> incomplete appointment{summary.total !== 1 ? 's' : ''} found.{' '}
                <Badge color="danger" className="me-1" data-cy="dex-readiness-no-record-badge">
                  {summary.no_session_record} no session record
                </Badge>
                <Badge color="warning" className="me-1" data-cy="dex-readiness-partial-badge">
                  {summary.partial_data} partial data
                </Badge>
                <div className="mt-1">
                  <Badge color="warning" data-cy="dex-readiness-mac-id-badge">
                    {summary.missing_mac_id ?? 0} appointment
                    {(summary.missing_mac_id ?? 0) !== 1 ? 's' : ''} missing My Aged Care ID
                  </Badge>
                </div>
              </>
            )}
          </Alert>
        )}
      </div>

      <div className="px-2 pb-2">
        <TableView
          id="dex-readiness-table"
          columns={readinessColumns}
          data={readinessRows}
          isLoading={isFetching}
          emptyMessage={
            data ? 'No appointments with missing DEX data in this date range.' : 'Enter a date range and click Search.'
          }
          readonly
          striped
          allowPagination
          paginationType="pages"
          totalCount={data?.total ?? 0}
          pageSize={PAGE_SIZE}
          currentPage={currentPage}
          onPageChange={handlePageChange}
          data-cy="dex-readiness-table"
        />
      </div>
    </div>
  );
};

export default DexReadinessPage;
